import { SkeletonBodyText, SkeletonDisplayText } from "@shopify/polaris";

import PageSkeleton from "./PageSkeleton";

/**
 * The loading picture for /survey-results. It replaces RouteLoader — a large centred
 * spinner with no heading — as this route's Suspense fallback in components/App.jsx
 * while the chunk downloads. See the header of PageSkeleton.jsx for the full write-up
 * of why a headingless spinner costs a layout jump the skeleton removes.
 *
 * ⚠️ THE SECOND GATE IS NOT YET WIRED TO THIS COMPONENT. routes/survey-results.jsx:136
 * still draws its own `if (loading)` picture — a centred progress bar and a rotating
 * status message — while the campaign query runs, so the sequence today is
 * skeleton → progress bar → page rather than the single unchanging frame
 * PageSkeleton.jsx describes. Rendering THIS from that branch is the remaining half of
 * the fix; it was left alone deliberately (that file was out of scope for this change).
 *
 * ⚠️ `heading` must stay byte-identical to routes/survey-results.jsx's own
 * `<s-page heading="Survey Results">` (routes/survey-results.jsx:138,174). That route
 * sets no `inlineSize`, so this one must not either.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THE REAL PAGE DRAWS, and what this mirrors
 * ─────────────────────────────────────────────────────────────────────────────
 *   1. `<s-section>` with an `<s-heading>Select a Campaign</s-heading>` and one
 *      `<s-select>` (routes/survey-results.jsx:175-203). → `sections={[1]}`: a small
 *      display text for the heading, one body line for the select.
 *   2. `<SurveyResults>`'s responses section (components/SurveyResults.jsx:803-889):
 *      a toolbar row ("Survey Responses" + a count badge on one side, Export CSV /
 *      Refresh on the other), then the `s-table`'s `filters` slot (a search field and
 *      a filter button in a `1fr auto` grid, SurveyResults.jsx:833), then the rows.
 *      → the `children` block below.
 *
 * DELIBERATELY NOT DRAWN:
 *   • The "Response Summary" section (NPS score + star-rating distribution,
 *     SurveyResults.jsx:730-801). It renders ONLY when responses exist AND at least
 *     one of them carries NPS or rating answers, so reserving space for it would be a
 *     guess — and a wrong one for every shop whose surveys are open-text.
 *   • The route's own loading progress bar (routes/survey-results.jsx:141-165). That
 *     is a loading affordance, not page content; a skeleton that previews another
 *     loading state previews nothing. Note it is a plain `role="progressbar"` div
 *     because there is NO `s-progress-bar` in this design system — if a bar is ever
 *     wanted here, copy that shape (and routes/billing.jsx:347-368), do not invent a
 *     web component.
 *
 * The row count is fixed at four: the table is variable-length, so this draws a
 * representative handful rather than pretending to know how many responses land.
 */
const TABLE_ROWS = 4;

export default function SurveyResultsSkeleton() {
  // NOTE: no `useAdminLoading()` call here — PageSkeleton already holds the admin
  // header progress bar for as long as it is mounted. A second claim would be a
  // second refcount owner released at the same instant, i.e. pure noise.
  return (
    <PageSkeleton heading="Survey Results" sections={[1]}>
      <s-section accessibilityLabel="Loading survey responses">
        <s-stack direction="block" gap="base">
          {/* Toolbar: heading + response-count badge, with the CSV / Refresh buttons opposite. */}
          <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="center">
            <SkeletonDisplayText size="small" />
            <s-box inlineSize="180px">
              <SkeletonBodyText lines={1} />
            </s-box>
          </s-grid>

          {/* The table's `filters` slot: a search field beside a single icon button. */}
          <s-grid gridTemplateColumns="1fr auto" gap="small-200" alignItems="center">
            <SkeletonBodyText lines={1} />
            <s-box inlineSize="40px">
              <SkeletonBodyText lines={1} />
            </s-box>
          </s-grid>

          {/* Rows, mirroring the three fixed columns: Email · Date/Time · Metadata.
              Question columns are appended per-campaign, so they are not drawn. */}
          <s-stack direction="block" gap="small">
            {Array.from({ length: TABLE_ROWS }, (_, i) => (
              <s-grid key={i} gridTemplateColumns="2fr 1fr 1fr" gap="base" alignItems="center">
                <SkeletonBodyText lines={1} />
                <SkeletonBodyText lines={1} />
                <SkeletonBodyText lines={1} />
              </s-grid>
            ))}
          </s-stack>
        </s-stack>
      </s-section>
    </PageSkeleton>
  );
}
